import { yupResolver } from '@hookform/resolvers/yup';
import type { DefaultValues, Resolver } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import type { ObjectSchema } from 'yup';
import type { ErrorResponse } from '@/types/apiTypes';
import type { CreateClientFormData } from '@/types/clientsApiTypes';
import { setBEFieldError } from '@/utils/setBEFieldError';
import type { SectionConfigFromRegistry } from '../../../../utils/sectionRegistry';
import { prefixErrorFields } from '../../utils/prefixErrorFields';
import { ModalShell } from '../ModalShell';

interface EditSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: CreateClientFormData;
  config: SectionConfigFromRegistry;
}

export function EditSectionModal({ isOpen, onClose, client, config }: EditSectionModalProps) {
  const form = useForm({
    defaultValues: config.mapClientToValues(client) as DefaultValues<Record<string, unknown>>,
    resolver: yupResolver(config.schema as ObjectSchema<Record<string, unknown>>) as Resolver<Record<string, unknown>>,
    mode: 'all',
  });

  const onSave = config.useOnSave() as (payload: unknown) => Promise<void>;

  const onSubmit = form.handleSubmit(async (data) => {
    const payload = config.mapValuesToPayload(data as never, client, {
      dirtyFields: form.formState.dirtyFields,
    });
    try {
      await onSave(payload);
    } catch (error) {
      setBEFieldError(prefixErrorFields(error as ErrorResponse<unknown>, config.errorFieldPrefix), form.setError);
      return;
    }
    onClose();
  });

  if (!isOpen) return null;

  return (
    <FormProvider {...form}>
      <ModalShell
        title={config.title}
        subtitle={config.subtitle}
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onSubmit}
        isSubmitting={form.formState.isSubmitting}
        canSubmit={form.formState.isValid}
      >
        <form className="space-y-0">
          <config.Fields />
        </form>
      </ModalShell>
    </FormProvider>
  );
}